import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ImageFileInfoEachSize } from "../../../../services/talkServices";
import { rentGoodsRental } from "../../../../services/goodsRentalServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import BottomGroup from "../../../../components/Button/Group";
import CommonButton from "../../../../components/Button";
import SpaceScannerPopup from "../../../../components/Popup/SpaceScanner";
import ServiceRentItemImage from "../../../../components/Service/Rent/ItemImage";
import ProgressIconGroup from "../../../../components/Service/Rent/ProgressIconGroup";
import { HttpStatusCode } from "axios";

type Props = {};

export default function ServiceRentalRequested(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [popup, setPopup] = useState("");
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [isLoading, setIsLoading] = useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const getGoodsImg = () => {
    return location?.state?.goodsImg?.items?.find(
      (item: ImageFileInfoEachSize) => item.scale === "small"
    )?.url;
  };

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="대여하기"
        onBackClick={() =>
          navigate(`/service/rental/${location?.state?.rentStockId}`)
        }
        className="px-5"
      />

      <div className="flex flex-col gap-10 items-center pb-5">
        <div className="px-5 h-[370px] w-full flex flex-col items-center justify-center gap-[30px] flex-shrink-0">
          <div className="flex flex-col py-2.5 justify-evenly items-center h-full">
            <ProgressIconGroup step={0} />
            <div className="flex flex-col gap-2.5 items-center">
              <div className=" text-2xl font-semibold leading-[140%]">
                예약 완료
              </div>
              <div className=" text-brand-neutral-70 text-sm leading-[18px] whitespace-pre-line text-center">
                {
                  "예약이 성공적으로 완료되었습니다.\n15분 이내로 대여 장소로 이동하여\n비치된 QR코드/NFC 를 인식시켜 주세요"
                }
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-5 flex flex-col gap-5">
          <div className="flex p-5 flex-col items-center justify-center gap-5 bg-brand-weather-badge-background rounded-[3px]">
            <div className="flex gap-2.5">
              <ServiceRentItemImage src={getGoodsImg()} name="item" />
              <div className="flex flex-col gap-2.5">
                <div className="h-[22px] self-stretch truncate text-[15px] font-medium leading-[22px]">
                  {location?.state?.goodsTypeName}
                </div>
                <div className="h-[31px] flex-[1_0_0] text-brand-neutral-40 text-xs leading-4 tracking-[-0.12px]">
                  {location?.state?.goodsDescription}
                </div>
              </div>
            </div>
            <HorizontalRule margin="0" className=" border-brand-neutral-20" />
            <div className=" text-brand-neutral-80 text-center text-sm leading-6 whitespace-pre-line">
              {
                "QR코드/NFC를 인식시켜 주세요\n*이용이 어려운 경우, 관리자에게 문의하세요"
              }
            </div>
          </div>
        </div>
      </div>

      <BottomGroup className="flex gap-1.5 px-5 py-2.5 h-[70px]">
        <CommonButton
          variant="tertiary"
          className="flex flex-[1_0_0] justify-center items-center px-4 py-[11px] border border-brand-primary-gray-20 rounded-[3px] text-brand-neutral-60"
          onClick={() => {
            setPopup("spaceScanner");
          }}
        >
          QR코드/NFC 인식
        </CommonButton>
        <CommonButton
          variant="tertiary"
          className="flex flex-[1_0_0] justify-center items-center px-4 py-[11px] border border-brand-primary-gray-20 rounded-[3px] text-brand-neutral-60"
          onClick={() => {
            if (location?.state?.goodsServiceId) {
              navigate(`/history/rent/${location?.state?.goodsServiceId}`, {
                state: {
                  parentUrl: `/service/rental/requested`,
                  parentState: { ...location?.state },
                },
              });
            } else {
              open("해당 물품 아이디 조회에 실패했습니다.");
            }
          }}
        >
          예약 내역 확인
        </CommonButton>
      </BottomGroup>

      <SpaceScannerPopup
        popup={popup}
        onClose={() => setPopup("")}
        title="대여하기"
        onResult={async (result) => {
          if (result) {
            let json;
            try {
              json = JSON.parse(result);
            } catch (e: any) {
              return open(`잘못된 장소 데이터입니다.` + JSON.stringify(e));
            }

            if (
              json.bldgId === location?.state?.buildingId &&
              json.storeId === location?.state?.storeId
            ) {
              setIsLoading(true);
              try {
                const result = await rentGoodsRental(
                  location?.state?.goodsServiceId as string,
                  json
                );

                if (result.status === HttpStatusCode.Ok) {
                  open(`대여 처리에 성공했습니다.`, {
                    onClick: async () => {
                      navigate("/service/rental/rented", {
                        state: result.data,
                      });
                    },
                  });
                } else {
                  open("대여에 실패했습니다." + JSON.stringify(result));
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open(`대여에 실패했습니다.`);
                }
              } finally {
                setIsLoading(false);
              }
            } else {
              open("대여를 신청한 장소가 아닙니다.\n대여 장소를 확인해주세요.");
            }
          }
        }}
      />
      {simplePopupComponent}
    </div>
  );
}
